import { FX_CARRY_LIMIT_DAYS } from '@/domain/fx'
import { formatMinor } from '~/api/client'
import { daysBetween } from '~/state/dates'
import { compactSignedMinor } from './format'

/**
 * Which sources the day was built from. A source that has stopped answering is
 * still in here — its positions are carried forward and its money is still in
 * the figure — so a short list means history that had not started yet, never a
 * source that is down.
 */
export interface PointCoverage {
  sources: string[]
  /**
   * Every one of the household's sources has data behind this day or held
   * nothing on it. A source in `partial` still counts: this speaks for sources,
   * and `partial` for the accounts inside them.
   */
  complete: boolean
  /**
   * Sources that held nothing on this day, so it is missing nothing of theirs.
   * Absent where there are none, as from a server that could not tell.
   */
  empty?: string[]
  /** Sources the day has data from, though not from every account they count. */
  partial?: PartialCoverage[]
}

export interface PartialCoverage {
  source: string
  /** Its accounts with no data by this day and no word that they held nothing. */
  missing: number
  /** Its accounts the day counts, so the shortfall can be put as one of two. */
  of: number
}

/**
 * How something arrives in the series, each a step that is not growth: a
 * source's history `begins`, a source `resumes` after days it was neither heard
 * from nor known to hold nothing, or an account neither heard from nor known to
 * hold nothing until then `joins` a source already present. A step out of
 * `empty` is none of these: money reaching an account that held nothing is a
 * real flow, and nets against wherever it left.
 */
export type ArrivalKind = 'begins' | 'resumes' | 'joins'

export interface SourceBeginning {
  source: string
  date: string
  /**
   * The subject's share of what arrived: the source's holdings on a `begins` or
   * `resumes`, the joining account's alone on a `joins`.
   */
  lookThroughMinor: number
  /** Absent means `begins`, the only arrival an older server could see. */
  kind?: ArrivalKind
  /** The account that arrived on a `joins`, so the step is put down to it, not its source. */
  accountId?: number
}

export interface SeriesCoverage {
  knownSources: string[]
  begins: SourceBeginning[]
  completeFrom: string | null
}

export interface NetWorthPoint {
  date: string
  lookThroughMinor: number
  spendableMinor: number
  liquidMinor: number
  taxMinor: number
  /** False where a company was left untaxed for want of rates, not because it owes nothing. */
  taxRatesConfigured: boolean
  projectedMinor: number
  /**
   * The part of `projectedMinor` in accounts of a source that had stopped
   * answering, so `stale` already marks it. It holds whatever that source was
   * modelling itself as well, since its rows do not say which were repeated.
   */
  carriedMinor?: number
  stale: boolean
  projected: boolean
  staleSince: string | null
  /** Whether this day's conversion used a rate published before it. */
  fxCarried: boolean
  /**
   * The sources whose last word by this day was that they had stopped
   * answering. One can be named with nothing carried for it, so this explains
   * a stretch while `carriedMinor` and `stale` decide how it is drawn.
   */
  staleSources?: string[]
  coverage: PointCoverage
}

export interface NetWorthSeries {
  baseCurrency: string
  from: string
  to: string
  coverage: SeriesCoverage
  /** Whether anything is held through an entity other than the household itself. */
  holdsThroughCompany: boolean
  /** Whether anything held cannot be sold; where false, Liquid repeats the line before it. */
  holdsUnsellable: boolean
  /** Which holdings the figures were valued over: every one, or only what can be sold. */
  holdings?: 'all' | 'sellable'
  /** The private-holdings switch the figures were built under: the one in Settings unless the request named its own. */
  includePrivate?: boolean
  /** What a view of the sellable holdings left out on its newest day; absent where that was nothing. */
  leftOut?: { date: string; lookThroughMinor: number }
  points: NetWorthPoint[]
}

export interface RangePreset {
  id: string
  label: string
  days: number | null
}

export const RANGE_PRESETS: readonly RangePreset[] = [
  { id: '30d', label: '30 days', days: 30 },
  { id: '90d', label: '90 days', days: 90 },
  { id: '1y', label: '1 year', days: 365 },
  { id: 'all', label: 'All', days: null },
]

export const DEFAULT_RANGE = '1y'

export function sliceRange<T>(points: readonly T[], rangeId: string): T[] {
  const preset = RANGE_PRESETS.find((r) => r.id === rangeId)
  if (!preset || preset.days === null) return [...points]
  return points.slice(Math.max(0, points.length - preset.days))
}

/**
 * Below the narrowest preset's own window, every preset slices to the same
 * days — there is nothing left for the control to tell apart. That is a fact
 * about how much history exists, not a fault in the control, and it resolves
 * itself the moment a later day pushes the total past this line.
 */
export function rangesIndistinguishable(totalDays: number): boolean {
  const narrowest = Math.min(
    ...RANGE_PRESETS.map((preset) => preset.days).filter((days): days is number => days !== null),
  )
  return totalDays <= narrowest
}

/**
 * Allocation has no series endpoint of its own, so the page reconstructs it by
 * asking for holdings on a handful of dates. Sampling keeps that bounded no
 * matter how long the range is, and the last date is always one of them because
 * it is the composition the reader came to see.
 */
export function sampleDates(dates: readonly string[], max: number): string[] {
  if (max < 1) return []
  if (dates.length <= max) return [...dates]

  const step = (dates.length - 1) / (max - 1)
  const picked = new Set<string>()
  for (let i = 0; i < max; i += 1) {
    const date = dates[Math.round(i * step)]
    if (date !== undefined) picked.add(date)
  }
  return [...picked]
}

export type ProvenanceKind = 'modelled' | 'carriedForward'

export interface ProvenanceSpan {
  kind: ProvenanceKind
  from: string
  to: string
  days: number
}

export interface PointProvenance {
  /** Value in the figure that rests on positions nobody confirmed. */
  modelled: boolean
  /** Positions repeated from an earlier day because the source stopped answering. */
  carriedForward: boolean
  flagged: boolean
  /** The unconfirmed value the carried-forward mark does not already account for. */
  modelledMinor: number
  /** The unconfirmed value under the carried-forward mark. */
  carriedMinor: number
}

/**
 * A stalled source repeats its positions unconfirmed, so they are in
 * `projectedMinor` as well as dated `stale`. Drawn from both, one stall would be
 * two marks and two notices; what it carried is taken out of the modelled part
 * so the cross-hatch is left for days where something else is unconfirmed too.
 */
export function provenanceOf(point: NetWorthPoint): PointProvenance {
  // Only on a day the carried mark is drawn, so a carried amount on a day not
  // dated stale is still drawn somewhere — as modelled.
  const carriedMinor = point.stale ? (point.carriedMinor ?? 0) : 0
  const modelledMinor = point.projectedMinor - carriedMinor
  // Not `> 0`: an unconfirmed value below zero moves the line as far as one above.
  const modelled = modelledMinor !== 0
  return {
    modelled,
    carriedForward: point.stale,
    flagged: modelled || point.stale,
    modelledMinor,
    carriedMinor,
  }
}

/**
 * What the tooltip says of a day, each mark with its own amount. A day old only
 * for its exchange rate is said to be that, not to have had its positions carried.
 *
 * `staleSince` is the earliest of everything old on the day: each stalled
 * source's last report, the oldest position, the rate. Put beside a named
 * source it would give that source a date that may be another's, so it is said
 * of the day unless only the positions could have dated it.
 */
export function provenanceSentence(point: NetWorthPoint, baseCurrency: string): string {
  const provenance = provenanceOf(point)
  const said: string[] = []
  if (provenance.modelled) {
    said.push(`${formatMinor(provenance.modelledMinor, baseCurrency)} of this is modelled, not fetched.`)
  }
  if (!provenance.carriedForward) return said.join(' ')

  const named = point.staleSources ?? []
  const attributed = named.length > 0 || provenance.carriedMinor !== 0
  // A rate is carried for a week at most, so a day dated earlier than that was
  // dated by its positions, whatever happened to the rate.
  const olderThanAnyRate =
    point.staleSince !== null && daysBetween(point.staleSince, point.date) > FX_CARRY_LIMIT_DAYS
  const datedByPositions = !attributed && (!point.fxCarried || olderThanAnyRate)

  if (datedByPositions) {
    said.push(`Positions carried forward since ${point.staleSince ?? 'an earlier day'}.`)
  } else if (attributed) {
    const what =
      provenance.carriedMinor !== 0
        ? `${formatMinor(provenance.carriedMinor, baseCurrency)} of this is`
        : 'Positions'
    const from = named.length > 0 ? ` from ${named.join(', ')}` : ''
    said.push(`${what} carried forward${from}.`)
  }
  if (point.fxCarried) said.push('Converted at an exchange rate published before this day.')
  if (!datedByPositions && point.staleSince !== null) {
    said.push(`The oldest part of this dates from ${point.staleSince}.`)
  }
  return said.join(' ')
}

/**
 * Whether a stretch was old because of its rates rather than its positions.
 *
 * Asked of the days in the span rather than of the household's sources, which
 * is what the page used to do: sources answer for today, and a span is a
 * stretch of days that may have gone wrong for different reasons at each end.
 */
export function fxCarriedWithin(
  points: readonly NetWorthPoint[],
  span: { from: string; to: string },
): boolean {
  return points.some(
    (point) => point.date >= span.from && point.date <= span.to && point.fxCarried,
  )
}

/**
 * The sources that had stopped answering on the days of a stretch, sorted.
 * Asked of the days for the same reason as `fxCarriedWithin`: a stretch months
 * old is explained by what was down then, not by whatever is down now.
 */
export function staleSourcesWithin(
  points: readonly NetWorthPoint[],
  span: { from: string; to: string },
): string[] {
  const named = new Set<string>()
  for (const point of points) {
    if (point.date < span.from || point.date > span.to) continue
    for (const source of point.staleSources ?? []) named.add(source)
  }
  return [...named].sort()
}

export function provenanceSpans(points: readonly NetWorthPoint[]): ProvenanceSpan[] {
  const kinds: ProvenanceKind[] = ['modelled', 'carriedForward']
  const spans: ProvenanceSpan[] = []

  for (const kind of kinds) {
    let open: ProvenanceSpan | undefined
    for (const point of points) {
      if (provenanceOf(point)[kind]) {
        if (open) {
          open.to = point.date
          open.days += 1
        } else {
          open = { kind, from: point.date, to: point.date, days: 1 }
        }
      } else if (open) {
        spans.push(open)
        open = undefined
      }
    }
    if (open) spans.push(open)
  }

  return spans
}

/**
 * An unconfirmed position the engine could not price is invisible in
 * `projectedMinor` — it contributes nothing rather than a wrong number — so the
 * only honest place to report it is next to the count of days it affects.
 */
export function unpricedProjectionDays(points: readonly NetWorthPoint[]): number {
  return points.filter((p) => p.projected && p.projectedMinor === 0).length
}

/**
 * A day where a source's history starts. Everything the source holds arrives at
 * once, so the figure steps by the whole of it — and that step is the source
 * being connected, not a day anybody earned anything.
 *
 * The sources on the first visible day are not beginnings: their history runs
 * off the left of the chart, and marking them there would invent an event out
 * of where the reader happened to start looking.
 */
export interface CoverageBoundary {
  date: string
  /** Sources whose history starts on this day, sorted. */
  sources: string[]
  /** The day before it in the same series, which the step is measured from. */
  previousDate: string
}

export function coverageBoundaries(points: readonly NetWorthPoint[]): CoverageBoundary[] {
  const first = points[0]
  if (!first) return []

  const boundaries: CoverageBoundary[] = []
  const seen = new Set(first.coverage?.sources ?? [])

  for (let i = 1; i < points.length; i += 1) {
    const point = points[i]
    const previous = points[i - 1]
    if (!point || !previous) continue

    const started = (point.coverage?.sources ?? []).filter((source) => !seen.has(source))
    if (started.length === 0) continue

    for (const source of started) seen.add(source)
    boundaries.push({ date: point.date, sources: [...started].sort(), previousDate: previous.date })
  }

  return boundaries
}

/**
 * What the boundaries visible in this range brought with them, taken from the
 * series' own measurement rather than from the step in the line: the step is the
 * whole day's move, and on the day a source arrives the rest of the household
 * moved too.
 */
export function arrivalsIn(
  boundaries: readonly CoverageBoundary[],
  begins: readonly SourceBeginning[],
): SourceBeginning[] {
  const visible = new Set(
    boundaries.flatMap((boundary) => boundary.sources.map((source) => `${boundary.date}/${source}`)),
  )
  return begins.filter((beginning) => visible.has(`${beginning.date}/${beginning.source}`))
}

/**
 * What these arrivals brought, summed from the series' own measurements. A
 * total that leaves out a beginning nobody measured is not what arrived, so
 * there is none where one is missing rather than a part quoted as the whole.
 */
function arrivedAt(
  boundaries: readonly CoverageBoundary[],
  arrivals: readonly SourceBeginning[],
): number | undefined {
  const unmeasured = boundaries.some((boundary) =>
    boundary.sources.some(
      (source) =>
        !arrivals.some((arrival) => arrival.date === boundary.date && arrival.source === source),
    ),
  )
  return unmeasured
    ? undefined
    : arrivals.reduce((totalMinor, arrival) => totalMinor + arrival.lookThroughMinor, 0)
}

/**
 * How much of the plot a beginning's label covers, as a share of the range: an
 * 11px label with its amount is about a quarter of the Dashboard's plot. A few
 * per cent would still overprint a week's beginnings at 90 days, where that
 * week is 7 % of the range.
 */
const LABEL_REACH = 0.25

/** The label on one beginning's line, or on a run too close to label apart. */
export interface BoundaryLabel {
  /** The line it is set against; the run's other lines are drawn unlabelled. */
  date: string
  /**
   * Right of its line, unless that would run it off the end of the plot, which
   * would cut the amount first: then left of the run's last line.
   */
  side: 'right' | 'left'
  text: string
}

/**
 * Each beginning labelled with what it brought, as the series measured it, so
 * the step in the line says it is a source arriving before anybody reads it as
 * money earned.
 */
export function boundaryLabels(
  boundaries: readonly CoverageBoundary[],
  arrivals: readonly SourceBeginning[],
  range: { from: string; to: string },
): BoundaryLabel[] {
  const span = Math.max(1, daysBetween(range.from, range.to))
  const at = (date: string): number => daysBetween(range.from, date) / span
  const fitsRightwards = (date: string): boolean => at(date) + LABEL_REACH <= 1

  const runs: CoverageBoundary[][] = []
  for (const boundary of boundaries) {
    const opened = runs.at(-1)
    if (opened?.[0] && at(boundary.date) - at(opened[0].date) < LABEL_REACH) opened.push(boundary)
    else runs.push([boundary])
  }
  // Only the last run can be too near the end to read rightwards. Read
  // leftwards from its last line, it reaches back towards the label before it,
  // and where it would land on that one the two share a label after all.
  const final = runs.at(-1)
  const before = runs.at(-2)
  if (final && before && !fitsRightwards(final[0].date)) {
    const reachesBackTo = at(final[final.length - 1].date) - LABEL_REACH
    if (reachesBackTo < at(before[0].date) + LABEL_REACH) {
      runs.pop()
      before.push(...final)
    }
  }

  return runs.flatMap((run): BoundaryLabel[] => {
    const first = run[0]
    const last = run[run.length - 1]
    if (!first || !last) return []

    const sources = run.flatMap((boundary) => boundary.sources).sort()
    const arrivedMinor = arrivedAt(run, arrivalsIn(run, arrivals))
    const named = `${sources.join(', ')} ${sources.length === 1 ? 'begins' : 'begin'}`
    const text = arrivedMinor === undefined ? named : `${named} · ${compactSignedMinor(arrivedMinor)}`

    return fitsRightwards(first.date)
      ? [{ date: first.date, side: 'right', text }]
      : [{ date: last.date, side: 'left', text }]
  })
}

/**
 * A stretch the newest day in view has sources for and this one does not. The
 * newest day is the yardstick because it is the figure every other one on the
 * chart is read against.
 */
export interface CoverageGap {
  from: string
  to: string
  days: number
  /** Sources the stretch is short of, sorted. */
  missing: string[]
}

export function coverageGaps(points: readonly NetWorthPoint[]): CoverageGap[] {
  const last = points[points.length - 1]
  if (!last) return []

  const yardstick = new Set(last.coverage?.sources ?? [])
  const gaps: CoverageGap[] = []
  let open: { from: string; to: string; days: number; missing: string } | undefined

  for (const point of points) {
    const have = new Set(point.coverage?.sources ?? [])
    const missing = [...yardstick].filter((source) => !have.has(source)).sort()
    const key = missing.join(',')

    if (missing.length === 0) {
      if (open) gaps.push({ ...open, missing: open.missing.split(',') })
      open = undefined
      continue
    }

    if (open && open.missing === key) {
      open.to = point.date
      open.days += 1
      continue
    }

    if (open) gaps.push({ ...open, missing: open.missing.split(',') })
    open = { from: point.date, to: point.date, days: 1, missing: key }
  }

  if (open) gaps.push({ ...open, missing: open.missing.split(',') })
  return gaps
}

/**
 * Below a hundredth of the figure, a source's arrival is a step nobody can see
 * in the line, and marking the days before it gave a sliver of the household
 * the same strip and notice as one that doubled it. Amends PRO-151.
 */
const MATERIAL_SHARE = 0.01

/**
 * The stretches whose missing sources would move the line: what the series
 * measured arriving with them, summed, against the newest day's look-through,
 * the measure arrivals are taken in. Summed as sizes, so a debt arriving beside
 * a holding cannot net it out of sight, and a figure below zero is as far from
 * nothing as its size. A source nobody measured is never assumed small, and
 * against a newest figure of nothing no arrival is, so every stretch is marked.
 */
export function materialGaps(
  points: readonly NetWorthPoint[],
  arrivals: readonly SourceBeginning[],
): CoverageGap[] {
  const newest = points[points.length - 1]
  if (!newest) return []
  // Only what arrived on the day a source began: a later account at the same
  // source is a step of its own, not what these days are without. A figure
  // that is not a number measured nothing.
  const measured = arrivalsIn(coverageBoundaries(points), arrivals).filter((arrival) =>
    Number.isFinite(arrival.lookThroughMinor),
  )
  const yardstickMinor = Math.abs(newest.lookThroughMinor) * MATERIAL_SHARE

  return coverageGaps(points).filter((gap) => {
    // Only a beginning after the stretch ends it. A source can drop out and
    // come back, and coming back is no beginning, so nobody measured it.
    const brought = measured.filter(
      (arrival) => arrival.date > gap.to && gap.missing.includes(arrival.source),
    )
    const unmeasured = gap.missing.some(
      (source) => !brought.some((arrival) => arrival.source === source),
    )
    const sizeMinor = brought.reduce((total, arrival) => total + Math.abs(arrival.lookThroughMinor), 0)
    return unmeasured || sizeMinor >= yardstickMinor
  })
}

export interface RangeChange {
  from: string
  to: string
  startMinor: number
  endMinor: number
  /**
   * The move between the ends less `arrivedMinor`, so a source being connected
   * is not quoted as money earned. What a source did after its first day is
   * still in here: that part of it is the money moving.
   */
  changeMinor: number
  /**
   * Sources whose history starts inside the range, sorted. Where there are any,
   * the two ends of the range are not the same household, so the distance
   * between them is not a change in the money until what arrived is taken out
   * — the false reading this whole apparatus exists to prevent.
   */
  joinedSources: string[]
  /**
   * What arrived inside the range, as the series measured it against the
   * accounts that brought it: the step in the line is the whole day's move, and
   * the rest of the household moved too. Absent where a source that began went
   * unmeasured, because taking out part of what arrived would quote a figure
   * that is neither the move nor the money; the change is then the plain move,
   * and its ends are only said to differ.
   */
  arrivedMinor?: number
  /** The sources `arrivedMinor` came with, sorted; none where it is absent. */
  arrivedWith: string[]
}

/**
 * `begins` are the series' own measurements, and it measures in look-through
 * alone: a change in another measure is asked without them, and its ends are
 * only said to differ. Every one dated inside the range is taken out, including
 * any that starts no source: an account arriving at a source already in view is
 * no more earned than the source arriving.
 */
export function changeOverRange(
  points: readonly NetWorthPoint[],
  measure: (point: NetWorthPoint) => number,
  begins: readonly SourceBeginning[] = [],
): RangeChange | undefined {
  const first = points[0]
  const last = points[points.length - 1]
  if (!first || !last || first === last) return undefined

  const boundaries = coverageBoundaries(points)
  const arrivals = begins.filter((arrival) => arrival.date > first.date && arrival.date <= last.date)
  const arrivedMinor = arrivedAt(boundaries, arrivals)
  const arrivedWith = arrivedMinor === undefined ? [] : arrivals.map((arrival) => arrival.source)
  const startMinor = measure(first)
  const endMinor = measure(last)
  return {
    from: first.date,
    to: last.date,
    startMinor,
    endMinor,
    changeMinor: endMinor - startMinor - (arrivedMinor ?? 0),
    joinedSources: boundaries.flatMap((boundary) => boundary.sources).sort(),
    ...(arrivedMinor === undefined ? {} : { arrivedMinor }),
    arrivedWith: [...new Set(arrivedWith)].sort(),
  }
}

/**
 * The band already covers a flagged run; the dots bracket it so the transition
 * into modelled data is visible even where the run is a single day wide.
 */
export function provenanceMarkerDates(spans: readonly ProvenanceSpan[]): Set<string> {
  const dates = new Set<string>()
  for (const span of spans) {
    dates.add(span.from)
    dates.add(span.to)
  }
  return dates
}
